import { getFinalResults } from "../../logic/divisionPlace";
import { getPoints } from "../../logic/coefficients/coefficients";
import {
  getBest3BenchKg,
  getBest3DeadliftKg,
  getBest3SquatKg,
  getFinalEventTotalKg,
  isEntryNoShow,
} from "../../logic/entry";
import { releaseVersion } from "../../versions";
import type { Entry } from "../../types/dataTypes";
import type { GlobalState } from "../../types/stateTypes";
import type { WrpfEntryAnchor, WrpfResultAttempt, WrpfResultsPayload } from "./types";
import {
  modalityPairsForEntry,
  equipmentForOpenlifterEvent,
  wrpfCompetitionWeightClass,
  wrpfDivisionFromString,
} from "./modalityMapping";
import { wrapsOnlyAffectSquatEnabled } from "../../logic/equipmentForEvent";

function mapAttempts(weights: ReadonlyArray<number>, statuses: ReadonlyArray<number>): Array<WrpfResultAttempt | null> {
  const out: Array<WrpfResultAttempt | null> = [];
  for (let i = 0; i < 4; i++) {
    const status = statuses[i];
    const weightKg = weights[i];
    if (status === undefined || status === 0) {
      out.push(null);
      continue;
    }
    out.push({
      weightKg: weightKg ?? 0,
      status: status === 1 ? 1 : -1,
    });
  }
  return out;
}

function mapOpenlifterSexToWrpf(sex: Entry["sex"]): string {
  if (sex === "F") return "WOMAN";
  if (sex === "Mx") return "MX";
  return "MAN";
}

function anchorForEntry(anchors: ReadonlyArray<WrpfEntryAnchor>, entryId: number): WrpfEntryAnchor | undefined {
  return anchors.find((a) => a.openlifterEntryId === entryId);
}

// Puesto por (entry, evento, división): una entry con dos divisiones aparece en DOS categorías de
// getFinalResults y cada una conserva su propio puesto (antes el mapa se pisaba por entry.id y
// quedaba el puesto de la última categoría iterada).
function placeKey(entryId: number, eventCode: string, division: string): string {
  return `${entryId}|${eventCode}|${division}`;
}

function buildPlaceMap(state: GlobalState): Map<string, number | null> {
  const map = new Map<string, number | null>();
  const categories = getFinalResults(state.registration.entries, state.meet);
  for (const { category, orderedEntries } of categories) {
    for (let i = 0; i < orderedEntries.length; i++) {
      const entry = orderedEntries[i];
      const total = getFinalEventTotalKg(state.meet, entry, category.event);
      const key = placeKey(entry.id, category.event, category.division);
      if (entry.guest || total === 0) {
        map.set(key, null);
      } else {
        map.set(key, i + 1);
      }
    }
  }
  return map;
}

export function buildResultsPayload(state: GlobalState, submitMode: "partial" | "final"): WrpfResultsPayload {
  const tournamentId = state.wrpfSession.tournamentId;
  if (!tournamentId) {
    throw new Error("WRPF session missing tournamentId");
  }

  const placeMap = buildPlaceMap(state);
  const anchors = state.wrpfSession.entryAnchors;
  const wrapsOnlyAffectSquat = wrapsOnlyAffectSquatEnabled(state.meet);

  const results = state.registration.entries.flatMap((entry) => {
    const anchor = anchorForEntry(anchors, entry.id);
    const walkIn = !anchor;
    const pairs = modalityPairsForEntry(entry, anchor, wrapsOnlyAffectSquat);

    // Categoría por el peso del PESAJE (autoritativo en powerlifting, igual que el rebuild de la
    // web); si no pesó, cae a la categoría inscripta (anchor) o a la intención de la entry.
    const weightClass = wrpfCompetitionWeightClass(entry, anchor) ?? (entry.sex === "F" ? "F_CAT82" : "M_CAT82");
    // Ausente (NS): sin puesto ni DQ. El peso del pesaje viene pre-llenado desde la inscripción, así
    // que en un NS no es real y no se publica (ni los puntos). Espejo de `entryIsNoShow` de la web.
    const noShow = isEntryNoShow(entry);

    return pairs.map((pair) => {
      // Código de evento OpenLifter de ESTA modalidad, para total/puntos/puesto por evento.
      const eventCode = pair.eventCode as Entry["events"][number];
      // String de división de la entry que corresponde a la división WRPF de la modalidad (así el
      // puesto se busca en la categoría correcta aun con strings legacy tipo "M40-44"). Sin match
      // (no debería pasar: los pares se validan contra las divisiones vivas) el puesto queda null —
      // nunca se toma prestado el puesto de otra categoría.
      const divisionStr = entry.divisions.find((d) => wrpfDivisionFromString(d) === pair.division) ?? "";

      const totalKg = getFinalEventTotalKg(state.meet, entry, eventCode);
      const points = getPoints(state.meet.formula, entry, eventCode, totalKg, state.meet.inKg);

      return {
        userOnTournamentId: anchor?.userOnTournamentId ?? null,
        userId: anchor?.userId ?? null,
        openlifterEntryId: entry.id,
        memberId: entry.memberId || null,
        walkInName: walkIn ? entry.name : null,
        walkInSex: walkIn ? mapOpenlifterSexToWrpf(entry.sex) : null,
        athleteName: entry.name,
        athleteCountry: entry.country || null,
        athleteProvince: entry.state || null,
        event: pair.event,
        division: pair.division,
        weightClass,
        sex: anchor?.sex ?? mapOpenlifterSexToWrpf(entry.sex),
        equipment: equipmentForOpenlifterEvent(entry, eventCode, wrapsOnlyAffectSquat),
        day: entry.day,
        platform: entry.platform,
        flight: entry.flight,
        lot: entry.lot || null,
        team: entry.team || null,
        bodyweightKg: noShow ? null : entry.bodyweightKg || null,
        attempts: {
          squat: mapAttempts(entry.squatKg, entry.squatStatus),
          bench: mapAttempts(entry.benchKg, entry.benchStatus),
          deadlift: mapAttempts(entry.deadliftKg, entry.deadliftStatus),
        },
        // Mejores levantamientos SÓLO del evento de esta fila (espejo del rebuild web): una fila
        // de banca sola no publica sentadilla/despegue.
        best: {
          squatKg: eventCode.includes("S") ? getBest3SquatKg(entry) || null : null,
          benchKg: eventCode.includes("B") ? getBest3BenchKg(entry) || null : null,
          deadliftKg: eventCode.includes("D") ? getBest3DeadliftKg(entry) || null : null,
          totalKg: totalKg || null,
        },
        place: placeMap.get(placeKey(entry.id, eventCode, divisionStr)) ?? null,
        points: !noShow && Number.isFinite(points) ? points : null,
        pointsFormula: state.meet.formula,
        disqualified: !noShow && totalKg === 0 && !entry.guest,
        noShow,
        guest: entry.guest === true,
      };
    });
  });

  return {
    schemaVersion: 1,
    tournamentId,
    submittedAt: new Date().toISOString(),
    openlifterVersion: releaseVersion,
    submitMode,
    results,
  };
}
